// src/ryan/admin/components/tabs/RyanAdminAnalyticsOutreach.tsx
//
// Fase 5 do analytics de funil — modo "Divulgação" dentro de RyanAdminAnalytics.tsx.
// Registro manual de envios (WhatsApp/outro) + alcance calculado por janela
// de tempo a partir de sentAt, cruzando SÓ com eventos source:'direct' (nunca
// 'referral', mesmo que caiam no mesmo período — é filtro por desenho, não
// coincidência de datas). Sem link único por envio: aproximação por janela é
// suficiente, decisão já tomada porque não há disparos múltiplos do mesmo
// canal no mesmo dia.
//
// O pill de origem (Total/Direto/Indicação) da tela-mãe NÃO se aplica aqui —
// o cálculo é sempre 'direct' por definição, não por escolha do usuário —
// por isso este modo não recebe `source` como prop.

import React, { useEffect, useState } from 'react';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
    fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

interface OutreachEntry {
    _id: string;
    channel: 'whatsapp_group' | 'whatsapp_private' | 'other';
    estimatedAudience: number;
    sentAt: string;
    note: string;
    reach: {
        visitCount: number;
        reachRatePercent: number;
        convertedCount: number;
        audienceConversionPercent: number;
    };
}

interface OutreachResponse {
    entries: OutreachEntry[];
    windowHours: number;
}

interface Props {
    productId: string;
    // Ver o mesmo comentário em RyanAdminAnalyticsTimeline.tsx — montado uma
    // única vez no componente pai, null enquanto um intervalo customizado
    // inválido (nesse estado, load() não dispara).
    periodParams: Record<string, string> | null;
}

const CHANNEL_LABELS: Record<string, string> = {
    whatsapp_group: 'Grupo de WhatsApp',
    whatsapp_private: 'Privado (WhatsApp)',
    other: 'Outro',
};

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Formato aceito por <input type="datetime-local"> — sem timezone, hora local.
function toDatetimeLocalValue(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const RyanAdminAnalyticsOutreach: React.FC<Props> = ({ productId, periodParams }) => {
    const [windowHours, setWindowHours] = useState(48);
    const [data, setData] = useState<OutreachResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const [channel, setChannel] = useState<'whatsapp_group' | 'whatsapp_private' | 'other'>('whatsapp_group');
    const [estimatedAudience, setEstimatedAudience] = useState('');
    const [sentAt, setSentAt] = useState(() => toDatetimeLocalValue(new Date()));
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const load = () => {
        if (!productId || !periodParams) return;
        setLoading(true);
        const qs = new URLSearchParams({ ...periodParams, windowHours: String(windowHours) }).toString();
        api(`/admin/analytics/${productId}/outreach?${qs}`)
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .finally(() => setLoading(false));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [productId, periodParams, windowHours]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaveError(null);
        const audience = Number(estimatedAudience);
        if (!Number.isFinite(audience) || audience < 1) {
            setSaveError('Informe um público estimado maior que zero.');
            return;
        }
        setSaving(true);
        try {
            const r = await api(`/admin/analytics/${productId}/outreach`, {
                method: 'POST',
                body: JSON.stringify({ channel, estimatedAudience: audience, sentAt: new Date(sentAt).toISOString(), note }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                setSaveError(d.message || 'Erro ao registrar envio.');
                return;
            }
            setEstimatedAudience('');
            setNote('');
            setSentAt(toDatetimeLocalValue(new Date()));
            load();
        } catch {
            setSaveError('Erro de conexão.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rana-outreach-wrap">
            <form className="rana-outreach-form" onSubmit={handleSubmit}>
                <select className="ras-input ras-input--sm" value={channel} onChange={e => setChannel(e.target.value as typeof channel)}>
                    <option value="whatsapp_group">Grupo de WhatsApp</option>
                    <option value="whatsapp_private">Privado (WhatsApp)</option>
                    <option value="other">Outro</option>
                </select>
                <input
                    className="ras-input ras-input--sm" type="number" min={1} placeholder="Público estimado"
                    value={estimatedAudience} onChange={e => setEstimatedAudience(e.target.value)} required
                />
                <input
                    className="ras-input ras-input--sm" type="datetime-local"
                    value={sentAt} onChange={e => setSentAt(e.target.value)} required
                />
                <input
                    className="ras-input" placeholder="Nota (opcional) — ex: nome do grupo"
                    value={note} onChange={e => setNote(e.target.value)}
                />
                <button type="submit" className="ras-grant-btn" disabled={saving}>
                    {saving ? 'Registrando...' : 'Registrar envio'}
                </button>
            </form>
            {saveError && <div className="rana-outreach-error">{saveError}</div>}

            <div className="rana-outreach-window">
                <label>
                    Janela de alcance:{' '}
                    <input
                        type="number" min={1} className="ras-input ras-input--sm rana-window-input"
                        value={windowHours} onChange={e => setWindowHours(Math.max(1, Number(e.target.value) || 48))}
                    /> horas
                </label>
            </div>

            {loading || !data ? (
                <div className="ras-spinner-wrap"><div className="ras-spinner" /></div>
            ) : data.entries.length === 0 ? (
                <div className="rana-empty">Nenhum envio registrado neste período.</div>
            ) : (
                <div className="rana-sessions-table-wrap">
                    <table className="rana-sessions-table">
                        <thead>
                            <tr>
                                <th>Enviado em</th><th>Canal</th><th>Público estimado</th>
                                <th>Visitas ({data.windowHours}h)</th><th>Alcance</th>
                                <th>Conversões</th><th>% do público</th><th>Nota</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.entries.map(entry => (
                                <tr key={entry._id}>
                                    <td>{formatDateTime(entry.sentAt)}</td>
                                    <td>{CHANNEL_LABELS[entry.channel]}</td>
                                    <td>{entry.estimatedAudience.toLocaleString('pt-BR')}</td>
                                    <td>{entry.reach.visitCount.toLocaleString('pt-BR')}</td>
                                    <td>{entry.reach.reachRatePercent}%</td>
                                    <td>{entry.reach.convertedCount.toLocaleString('pt-BR')}</td>
                                    <td>{entry.reach.audienceConversionPercent}%</td>
                                    <td className="rana-outreach-note">{entry.note || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RyanAdminAnalyticsOutreach;
